import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import AttitudesPage from './components/AttitudesPage';
import AttitudesReport from './components/UserInfoAndAttitudes';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import Login from './components/Login';
import ApiLogin from './components/ApiLogin';
import { ThemeContext } from './contexts/ThemeContext';
import useToken from './hooks/useToken';
import Admin from './components/Admin';
import TestReviewPage from './components/TestReviewPage';
import Layout from './components/Layout';
import Preferences from './generics/Preferences';
import Graphs from './components/Graphs';
import UserInfo from './components/UserInfo';
import UserManager from './components/UserManager';
import CompanyManager from './components/CompanyManager';
import ScopePDFReport from './components/scopePDFReport';
import SEO from './components/SEO';
import Blog from './components/Blog';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PWAUpdateNotification from './components/PWAUpdateNotification';
import PushNotificationPrompt from './components/PushNotificationPrompt';
import Analytics from './components/Analytics/Analytics';
import RouteTracker from './components/RouteTracker';

// Configure global axios interceptor for 401 handling
import './utils/axiosConfig';

import './App.css';


function App() {
    const [themeValue, setThemeValue] = useState("day");
    const { token, setToken/*, code*/ } = useToken();

    // Set <html lang> dynamically for SEO and accessibility
    React.useEffect(() => {
        const supportedLangs = ['en', 'fr', 'de', 'it', 'es'];
        // Use sessionStorage language, fallback to browser, then 'en'
        const stored = sessionStorage.getItem('language');
        let lang = 'en';
        if (stored && supportedLangs.includes(stored)) lang = stored;
        else {
            const browserLang = navigator.language?.slice(0, 2);
            if (browserLang && supportedLangs.includes(browserLang)) lang = browserLang;
        }
        document.documentElement.lang = lang;
    }, []);

    //console.log("Current code:", code);

    if (!token) {
        return (
            <HelmetProvider>
                <ThemeContext.Provider value={{ themeValue, setThemeValue }}>
                    <BrowserRouter>
                    <RouteTracker />
                    <SEO />
                    <Preferences />
                <Switch>
                    <Route exact path="/">
                        <Login setToken={setToken} context="login" />
                    </Route>
                    <Route path="/key/:keyParam">
                        <ApiLogin setToken={setToken} context="login" />
                    </Route>
                    <Route path="/manager">
                        <Login setToken={setToken} />
                    </Route>
                    <Route path="/test-review">
                        <Login setToken={setToken} />
                    </Route>
                    <Route path="/scopePDFReport/:testRef/:auth" component={ScopePDFReport} />
                    <Route path="/userInfo"><Login setToken={setToken} /></Route>
                    <Route path="/userInfo/edit"><Login setToken={setToken} /></Route>
                    <Route path="/userManager"><Login setToken={setToken} /></Route>
                    <Route path="/companyManager"><Login setToken={setToken} /></Route>
                    <Route path="/results">
                        <Login setToken={setToken} context="results" />
                    </Route>
                    <Route path="/blog">
                        <Blog />
                    </Route>
                    <Redirect to="/manager" />
                </Switch>
            </BrowserRouter>
            </ThemeContext.Provider>
            </HelmetProvider>
        );
    }
    return (
        <HelmetProvider>
            <div className="App">
                <BrowserRouter>
                    <RouteTracker />
                    <SEO />
                    <Switch>
                    <Route exact path="/">
                        <ThemeContext.Provider value={{ themeValue, setThemeValue }}>
                            <Layout token={token} />
                        </ThemeContext.Provider>
                    </Route>
                    <Route path="/manager">
                        <Admin />
                    </Route>
                    <Route path="/test-review">
                        <TestReviewPage token={token} />
                    </Route>
                    <Route path="/attitudes" render={({ location, history }) => {
                        const user = location.state?.user;
                        return (
                            <AttitudesPage
                                userId={user?._id}
                                onBack={() => history.goBack()}
                                user={user}
                            />
                        );
                    }} />
                    <Route path="/userInfoAndAttitudes" render={({ location, history }) => {
                        const user = location.state?.user;
                        return (
                            <AttitudesReport
                                userId={user?._id}
                                onBack={() => history.goBack()}
                                user={user}
                            />
                        );
                    }} />
                    <Route path="/scopePDFReport/:testRef/:auth" component={ScopePDFReport} />
                    <Route path="/userInfo" component={UserInfo} />
                    <Route path="/userInfo/edit" component={UserInfo} />
                    <Route path="/userManager" component={UserManager} />
                    <Route path="/companyManager" component={CompanyManager} />
                    <Route path="/results">
                        <ThemeContext.Provider value={{ themeValue, setThemeValue }}>
                            <Preferences />
                            <Graphs token={token} />
                        </ThemeContext.Provider>
                    </Route>
                    <Route path="/blog">
                        <ThemeContext.Provider value={{ themeValue, setThemeValue }}>
                            <Preferences />
                            <Blog />
                        </ThemeContext.Provider>
                    </Route>
                    <Route path="/analytics">
                        <Analytics />
                    </Route>
                    <Redirect to="/" />
                </Switch>
            </BrowserRouter>
            
            {/* PWA Components - Only for superadmin (code 1000) */}
            {sessionStorage.getItem('code') === '1000' && (
                <>
                    <PWAInstallPrompt />
                    <PWAUpdateNotification />
                    <PushNotificationPrompt />
                </>
            )}
        </div>
        </HelmetProvider>
    );
}

export default App;